import React from "react";
import { trackError } from "../utils/healthReporter";

/**
 * Per-feature Error Boundary — wraps individual POS sections.
 *
 * When a child component throws, only THIS section shows the error card.
 * Rest of POS continues working. User can click "Retry" to re-mount.
 *
 * Usage:
 *   <FeatureErrorBoundary name="Cart">
 *     <CartSection />
 *   </FeatureErrorBoundary>
 */
class FeatureErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const name = this.props.name || "Unknown";
    console.error(`[FeatureErrorBoundary:${name}]`, error?.message, errorInfo?.componentStack?.slice(0, 200));
    trackError();

    // Log to observability if available
    try {
      if (window.electronAPI?.observability?.log) {
        window.electronAPI.observability.log("error", "feature-crash", `${name}: ${error?.message}`, {
          feature: name,
          stack: error?.stack?.slice(0, 500),
          componentStack: errorInfo?.componentStack?.slice(0, 500),
        });
      }
    } catch {}
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback from props
      if (this.props.fallback) {
        return this.props.fallback({ error: this.state.error, retry: this.handleRetry });
      }

      // Default inline error card
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          padding: "24px 16px", minHeight: 120, background: "#fef2f2", borderRadius: 8,
          border: "1px solid #fecaca", margin: 8,
        }}>
          <div style={{ fontSize: 13, color: "#991b1b", fontWeight: 600, marginBottom: 4 }}>
            {this.props.name || "Section"} Error
          </div>
          <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 12, textAlign: "center", maxWidth: 300 }}>
            {this.state.error?.message?.slice(0, 100) || "Something went wrong"}
          </div>
          <button
            onClick={this.handleRetry}
            style={{
              padding: "6px 16px", fontSize: 12, fontWeight: 600,
              background: "#fff", border: "1px solid #d1d5db", borderRadius: 6,
              cursor: "pointer", color: "#374151",
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default FeatureErrorBoundary;
